<template>
  <b-modal
    :visible="visible"
    title="Bibliothèque d'images"
    size="xl"
    centered
    no-fade
    hide-footer
    body-class="image-library-body p-0"
    @change="$emit('update:visible', $event)"
    @hide="onHide"
  >
    <b-tabs
      v-model="activeTab"
      pills
      nav-class="image-library__tabs"
      content-class="image-library__tabs-content"
    >
      <b-tab title="Bibliothèque" active>
        <div
          class="image-library"
          :class="{ 'image-library--drop-target': isDragOver }"
          @dragenter.prevent="onDragEnter"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
        >
          <div class="image-library__toolbar">
            <b-form-input
              v-model="search"
              size="sm"
              placeholder="Rechercher par nom de fichier…"
              class="image-library__search"
            />
            <b-form-checkbox v-model="onlyOrphans" class="ml-2">
              Orphelines uniquement
            </b-form-checkbox>
            <input
              ref="fileInput"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              class="d-none"
              @change="onFileInputChange"
            >
            <b-button
              variant="primary"
              size="sm"
              class="ml-2"
              :disabled="!!uploading"
              @click="triggerFilePicker"
            >
              <b-spinner v-if="uploading" small />
              <feather-icon v-else icon="UploadIcon" /> Uploader
            </b-button>
            <div class="ml-auto text-muted small">
              {{ filtered.length }} / {{ items.length }} image{{ items.length > 1 ? 's' : '' }}
            </div>
          </div>

          <div v-if="isDragOver" class="image-library__drop-hint">
            <feather-icon icon="UploadCloudIcon" size="48" />
            <p class="mt-2 mb-0">Déposez vos images ici pour les ajouter</p>
            <small class="text-muted">PNG, JPEG, WebP ou GIF — 5 Mo max par image</small>
          </div>

          <div v-if="uploadErrors.length" class="image-library__errors">
            <small class="text-danger">
              <strong>Échec upload :</strong>
              <span v-for="(err, i) in uploadErrors" :key="i" class="ml-1">
                {{ err }}<span v-if="i < uploadErrors.length - 1">,</span>
              </span>
            </small>
          </div>

          <div class="image-library__content">
            <div v-if="loading" class="text-center p-3 w-100">
              <b-spinner variant="primary" /> Chargement…
            </div>

            <template v-else>
              <div class="image-library__grid">
                <div
                  v-for="item in filtered"
                  :key="item.name"
                  class="image-thumb"
                  :class="{ 'image-thumb--selected': selected && selected.name === item.name }"
                  :title="item.name"
                  @click="selectItem(item)"
                  @dblclick="insertItem(item)"
                >
                  <div class="image-thumb__preview">
                    <img
                      v-if="thumbnails[item.name]"
                      :src="thumbnails[item.name]"
                      :alt="item.name"
                      loading="lazy"
                    >
                    <div v-else class="image-thumb__placeholder">
                      <feather-icon icon="ImageIcon" size="32" />
                    </div>
                  </div>
                  <div class="image-thumb__name">{{ item.name }}</div>
                  <b-badge
                    v-if="!item.references.length"
                    variant="light-warning"
                    class="image-thumb__badge"
                  >
                    Orpheline
                  </b-badge>
                </div>

                <div v-if="!filtered.length" class="text-center p-3 w-100 text-muted">
                  <feather-icon icon="InboxIcon" size="32" />
                  <p class="mb-0 mt-2">Aucune image ne correspond.</p>
                  <small>Glissez-déposez des images ici, ou cliquez sur « Uploader ».</small>
                </div>
              </div>

              <div v-if="selected" class="image-library__detail">
                <h6 class="mb-2">{{ selected.name }}</h6>
                <div class="text-center mb-2">
                  <img
                    v-if="thumbnails[selected.name]"
                    :src="thumbnails[selected.name]"
                    :alt="selected.name"
                    class="image-library__detail-preview"
                  >
                </div>
                <dl class="image-library__meta">
                  <dt>Taille</dt>
                  <dd>{{ formatSize(selected.size) }}</dd>
                  <template v-if="selected.width && selected.height">
                    <dt>Dimensions</dt>
                    <dd>{{ selected.width }} × {{ selected.height }} px</dd>
                  </template>
                  <dt>Type</dt>
                  <dd>{{ selected.mime }}</dd>
                  <dt>Références</dt>
                  <dd>
                    <span v-if="!selected.references.length" class="text-warning">
                      Orpheline (aucune référence)
                    </span>
                    <ul v-else class="image-library__refs">
                      <li v-for="ref in selected.references" :key="ref">
                        <code>{{ ref }}</code>
                      </li>
                    </ul>
                  </dd>
                </dl>
                <b-button
                  variant="primary"
                  block
                  :disabled="!selected"
                  @click="insertItem(selected)"
                >
                  <feather-icon icon="PlusIcon" /> Insérer dans l'éditeur
                </b-button>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  block
                  class="mt-1"
                  :disabled="deleting"
                  @click="confirmDelete(selected)"
                >
                  <b-spinner v-if="deleting" small />
                  <feather-icon v-else icon="Trash2Icon" /> Supprimer
                </b-button>
              </div>
            </template>
          </div>
        </div>
      </b-tab>

      <b-tab title="Audit">
        <div class="audit-panel">
          <div v-if="auditLoading" class="text-center p-3">
            <b-spinner variant="primary" /> Analyse en cours…
          </div>

          <template v-else-if="audit">
            <div class="audit-section">
              <div class="audit-section__header">
                <h6 class="mb-0">
                  Images orphelines
                  <b-badge variant="light-warning">{{ audit.orphans.length }}</b-badge>
                </h6>
                <small class="text-muted">Fichiers présents dans <code>_images/</code> qu'aucun article ne référence.</small>
              </div>
              <p v-if="!audit.orphans.length" class="text-muted small mb-0">
                Toutes les images sont référencées. ✓
              </p>
              <ul v-else class="audit-list">
                <li v-for="name in audit.orphans" :key="name">
                  <code>{{ name }}</code>
                </li>
              </ul>
            </div>

            <div class="audit-section">
              <div class="audit-section__header">
                <h6 class="mb-0">
                  Références cassées
                  <b-badge variant="light-danger">{{ audit.broken_refs.length }}</b-badge>
                </h6>
                <small class="text-muted">Articles qui pointent sur une image absente de <code>_images/</code>.</small>
              </div>
              <p v-if="!audit.broken_refs.length" class="text-muted small mb-0">
                Aucune référence cassée. ✓
              </p>
              <ul v-else class="audit-list">
                <li v-for="ref in audit.broken_refs" :key="(ref.is_proposal ? 'p:' : 'a:') + ref.slug">
                  <code>{{ ref.is_proposal ? '_proposals/' + ref.slug : ref.slug }}</code>
                  <small class="text-muted">
                    manque :
                    <span v-for="(m, i) in ref.missing" :key="m">
                      <code>{{ m }}</code><span v-if="i < ref.missing.length - 1">, </span>
                    </span>
                  </small>
                </li>
              </ul>
            </div>

            <div class="audit-section">
              <div class="audit-section__header">
                <div>
                  <h6 class="mb-0">
                    URLs externes
                    <b-badge variant="light-info">{{ externalUrlCount }}</b-badge>
                  </h6>
                  <small class="text-muted">Images servies depuis un domaine externe (Intercom, etc.) — à rapatrier en local.</small>
                </div>
                <b-button
                  v-if="externalUrlCount > 0"
                  variant="primary"
                  size="sm"
                  :disabled="importingBulk"
                  @click="importAllExternalUrls"
                >
                  <b-spinner v-if="importingBulk" small />
                  <feather-icon v-else icon="DownloadCloudIcon" />
                  Importer toutes les images distantes
                </b-button>
              </div>
              <div v-if="bulkProgress" class="audit-progress small text-muted mb-1">
                {{ bulkProgress.done }} / {{ bulkProgress.total }}
                <span v-if="bulkProgress.failed.length"> — {{ bulkProgress.failed.length }} échec(s)</span>
              </div>
              <div v-if="bulkErrors.length" class="image-library__errors mb-1">
                <small class="text-danger">
                  <strong>Échec(s) :</strong>
                  <span v-for="(err, i) in bulkErrors.slice(0, 5)" :key="i" class="ml-1">
                    {{ err }}<span v-if="i < Math.min(bulkErrors.length, 5) - 1">,</span>
                  </span>
                  <span v-if="bulkErrors.length > 5"> … et {{ bulkErrors.length - 5 }} autre(s).</span>
                </small>
              </div>
              <p v-if="!audit.external_refs.length" class="text-muted small mb-0">
                Aucune image externe à rapatrier. ✓
              </p>
              <ul v-else class="audit-list">
                <li v-for="ref in audit.external_refs" :key="(ref.is_proposal ? 'p:' : 'a:') + ref.slug">
                  <div>
                    <code>{{ ref.is_proposal ? '_proposals/' + ref.slug : ref.slug }}</code>
                    <span class="text-muted small"> — {{ ref.urls.length }} URL{{ ref.urls.length > 1 ? 's' : '' }}</span>
                  </div>
                  <ul class="audit-list audit-list--nested">
                    <li v-for="url in ref.urls" :key="url">
                      <a :href="url" target="_blank" rel="noopener noreferrer" class="audit-url">{{ url }}</a>
                      <b-button
                        v-if="!isImportedUrl(ref, url)"
                        variant="link"
                        size="sm"
                        class="audit-import-btn"
                        :disabled="!!isImportingUrl(ref, url) || importingBulk"
                        @click="importOneUrl(ref, url)"
                      >
                        <b-spinner v-if="isImportingUrl(ref, url)" small />
                        <feather-icon v-else icon="DownloadIcon" /> Importer
                      </b-button>
                      <b-badge v-else variant="light-success" class="ml-1">Importée</b-badge>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </template>

          <div v-else class="text-muted text-center p-3">
            <feather-icon icon="ActivityIcon" size="32" />
            <p class="mt-2 mb-2">Audit non chargé.</p>
            <b-button variant="outline-primary" size="sm" @click="loadAudit">
              Lancer l'analyse
            </b-button>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<script>
// Modale "Bibliotheque d'images" — Phase 2 (lecture) + Phase 3 (upload/delete).
//
// Phase 2 (deja livre) :
// - Grille des vignettes de articles/_images/ avec recherche + filtre orphelines
// - Panneau de detail (taille, dimensions, MIME, refs entrantes)
// - Insertion 1-clic dans l'editeur via @insert
//
// Phase 3 :
// - Bouton "Uploader" + drag-and-drop sur tout le contenu de la modale.
//   Multi-fichiers acceptes. Validation MIME cote serveur (signature
//   bytes, pas Content-Type) — on accepte ici png/jpeg/webp/gif (pas SVG).
// - Bouton "Supprimer" dans le panneau detail. Si l'image a des refs
//   entrantes, le backend renvoie 409 BUXIDA_DOCS_IMAGE_IN_USE et le
//   front demande confirmation pour force=1.
//
// Auth : la route /admin/docs/images/static/:name exige un JWT, et
// <img src> n'embarque pas d'Authorization. On telecharge donc chaque
// vignette via axios responseType: 'blob' + URL.createObjectURL, et
// on revoque les URLs a la fermeture pour eviter les fuites memoire.

import {
  BModal,
  BFormInput,
  BFormCheckbox,
  BSpinner,
  BButton,
  BBadge,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import axios from '@axios'

const DOCS_API_BASE = 'https://api2.buxida.com'

export default {
  name: 'ImageLibraryModal',
  components: {
    BModal,
    BFormInput,
    BFormCheckbox,
    BSpinner,
    BButton,
    BBadge,
    BTabs,
    BTab,
  },
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      items: [],
      thumbnails: {}, // { name -> objectURL }
      search: '',
      onlyOrphans: false,
      selected: null,
      uploading: 0, // count of in-flight uploads
      uploadErrors: [],
      deleting: false,
      isDragOver: false,
      dragDepth: 0,
      // Phase 4 — onglet Audit
      activeTab: 0,
      audit: null, // { orphans, broken_refs, external_refs }
      auditLoading: false,
      // Suivi des URLs externes en cours d'import (Set serialise par cle stable
      // `slug|is_proposal|url` car Vue 2 ne reagit pas aux Set natifs — on
      // utilise un objet plain reactif via $set/$delete).
      importingUrls: {},
      // Map des URLs deja importees (meme cle), pour griser la ligne et
      // afficher un badge "Importee" sans recharger l'audit a chaque ligne.
      importedUrls: {},
      importingBulk: false,
      bulkProgress: null, // { done, total, failed: [] }
      bulkErrors: [],
    }
  },
  computed: {
    filtered() {
      const q = (this.search || '').trim().toLowerCase()
      return this.items.filter(item => {
        if (this.onlyOrphans && item.references.length > 0) return false
        if (q && !item.name.toLowerCase().includes(q)) return false
        return true
      })
    },
    externalUrlCount() {
      if (!this.audit || !this.audit.external_refs) return 0
      return this.audit.external_refs.reduce((acc, r) => acc + r.urls.length, 0)
    },
  },
  watch: {
    visible(val) {
      if (val) this.load()
    },
    activeTab(val) {
      // Lazy-load l'audit a la premiere activation de l'onglet, et a
      // chaque retour si l'audit a ete reset (apres bulk import).
      if (val === 1 && !this.audit && !this.auditLoading) this.loadAudit()
    },
  },
  beforeDestroy() {
    this.revokeThumbnails()
  },
  methods: {
    async load() {
      this.loading = true
      this.selected = null
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/images`)
        const data = response.data || {}
        if (data.res === 1 && Array.isArray(data.items)) {
          this.items = data.items
          this.loadAllThumbnails()
        } else {
          this.$emit('error', (data && data.detail) || 'Réponse inattendue')
        }
      } catch (err) {
        this.$emit('error', this.formatErr(err))
      } finally {
        this.loading = false
      }
    },
    async loadAllThumbnails() {
      const promises = this.items.map(item => this.loadOneThumbnail(item.name))
      await Promise.all(promises)
    },
    async loadOneThumbnail(name) {
      if (this.thumbnails[name]) return
      try {
        const response = await axios.get(
          `${DOCS_API_BASE}/admin/docs/images/static/${encodeURIComponent(name)}`,
          { responseType: 'blob' },
        )
        const url = URL.createObjectURL(response.data)
        this.$set(this.thumbnails, name, url)
      } catch (_) { /* placeholder */ }
    },
    revokeThumbnails() {
      Object.values(this.thumbnails).forEach(url => {
        try { URL.revokeObjectURL(url) } catch (_) { /* ignore */ }
      })
      this.thumbnails = {}
    },
    selectItem(item) {
      this.selected = item
    },
    insertItem(item) {
      this.$emit('insert', item)
      this.$emit('update:visible', false)
    },

    // ---------- Upload ----------
    triggerFilePicker() {
      if (this.$refs.fileInput) this.$refs.fileInput.click()
    },
    onFileInputChange(ev) {
      const files = Array.from((ev && ev.target && ev.target.files) || [])
      this.uploadFiles(files)
      // Reset l'input pour permettre de re-uploader le meme fichier
      if (ev.target) ev.target.value = ''
    },
    onDragEnter() {
      this.dragDepth += 1
      this.isDragOver = true
    },
    onDragOver(ev) {
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave() {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.isDragOver = false
    },
    onDrop(ev) {
      this.dragDepth = 0
      this.isDragOver = false
      const files = Array.from((ev.dataTransfer && ev.dataTransfer.files) || [])
        .filter(f => f && f.type && f.type.startsWith('image/'))
      this.uploadFiles(files)
    },
    uploadFiles(files) {
      if (!files || !files.length) return Promise.resolve()
      this.uploadErrors = []
      // Upload sequentiel pour preserver l'ordre d'ajout dans this.items
      // (unshift). reduce + chaining de promesses : pas de for+await
      // (eslint no-restricted-syntax / no-await-in-loop).
      return files.reduce(
        (p, file) => p.then(() => this.uploadOneFile(file)),
        Promise.resolve(),
      )
    },
    uploadOneFile(file) {
      this.uploading += 1
      const form = new FormData()
      form.append('file', file, file.name)
      return axios
        .post(`${DOCS_API_BASE}/admin/docs/images`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(response => {
          const data = response.data || {}
          if (data.res === 1 && data.item) {
            this.items.unshift(data.item)
            this.loadOneThumbnail(data.item.name)
          } else {
            this.uploadErrors.push(`${file.name}: ${(data && data.detail) || 'erreur inconnue'}`)
          }
        })
        .catch(err => {
          this.uploadErrors.push(`${file.name}: ${this.formatErr(err)}`)
        })
        .finally(() => {
          this.uploading -= 1
        })
    },

    // ---------- Delete ----------
    async confirmDelete(item) {
      if (!item) return
      const inUse = item.references && item.references.length > 0
      const msg = inUse
        ? `L'image "${item.name}" est référencée par ${item.references.length} article(s) : `
          + `${item.references.slice(0, 5).join(', ')}\n\n`
          + 'Supprimer quand même ? Les références deviendront des liens cassés.'
        : `Supprimer définitivement "${item.name}" ?`
      const ok = await this.$bvModal.msgBoxConfirm(msg, {
        title: 'Confirmer la suppression',
        okTitle: inUse ? 'Forcer la suppression' : 'Supprimer',
        okVariant: 'danger',
        cancelTitle: 'Annuler',
        cancelVariant: 'outline-secondary',
        centered: true,
      })
      if (!ok) return
      this.doDelete(item, inUse)
    },
    async doDelete(item, force) {
      this.deleting = true
      try {
        const url = `${DOCS_API_BASE}/admin/docs/images/${encodeURIComponent(item.name)}${force ? '?force=1' : ''}`
        const response = await axios.delete(url)
        const data = response.data || {}
        if (data.res === 1) {
          // Retire de la liste + revoque le blob
          const idx = this.items.findIndex(i => i.name === item.name)
          if (idx !== -1) this.items.splice(idx, 1)
          if (this.thumbnails[item.name]) {
            try { URL.revokeObjectURL(this.thumbnails[item.name]) } catch (_) { /* ignore */ }
            this.$delete(this.thumbnails, item.name)
          }
          if (this.selected && this.selected.name === item.name) this.selected = null
        } else {
          this.$emit('error', (data && data.detail) || 'Suppression échouée')
        }
      } catch (err) {
        this.$emit('error', this.formatErr(err))
      } finally {
        this.deleting = false
      }
    },

    onHide() {
      this.revokeThumbnails()
      this.search = ''
      this.onlyOrphans = false
      this.selected = null
      this.uploadErrors = []
      this.isDragOver = false
      this.dragDepth = 0
      // Phase 4 — reset audit / imports
      this.activeTab = 0
      this.audit = null
      this.auditLoading = false
      this.importingUrls = {}
      this.importedUrls = {}
      this.importingBulk = false
      this.bulkProgress = null
      this.bulkErrors = []
    },

    // ---------- Audit (Phase 4) ----------
    refKey(ref, url) {
      return `${ref.is_proposal ? 'p' : 'a'}|${ref.slug}|${url}`
    },
    isImportingUrl(ref, url) {
      return !!this.importingUrls[this.refKey(ref, url)]
    },
    isImportedUrl(ref, url) {
      return !!this.importedUrls[this.refKey(ref, url)]
    },
    async loadAudit() {
      this.auditLoading = true
      try {
        const response = await axios.get(`${DOCS_API_BASE}/admin/docs/images/audit`)
        const data = response.data || {}
        if (data.res === 1 && data.result) {
          this.audit = data.result
          // Reset l'etat "deja importee" : un nouvel audit reflete la realite.
          this.importedUrls = {}
        } else {
          this.$emit('error', (data && data.detail) || 'Audit échoué')
        }
      } catch (err) {
        this.$emit('error', this.formatErr(err))
      } finally {
        this.auditLoading = false
      }
    },
    async importOneUrl(ref, url) {
      const key = this.refKey(ref, url)
      if (this.importingUrls[key] || this.importedUrls[key]) return null
      this.$set(this.importingUrls, key, true)
      try {
        const response = await axios.post(`${DOCS_API_BASE}/admin/docs/images/import-from-url`, {
          slug: ref.slug,
          url,
          is_proposal: ref.is_proposal,
        })
        const data = response.data || {}
        if (data.res === 1 && data.item) {
          this.$set(this.importedUrls, key, data.item.name)
          // Ajoute l'image fraichement importee a la bibliotheque pour qu'elle
          // soit immediatement disponible dans l'onglet Bibliotheque sans
          // recharger. References = [slug_label] puisqu'on vient de reecrire le MD.
          const label = ref.is_proposal ? `_proposals/${ref.slug}` : ref.slug
          this.items.unshift({
            name: data.item.name,
            size: data.item.size,
            mtime: new Date().toISOString(),
            mime: data.item.mime,
            width: data.item.width,
            height: data.item.height,
            references: [label],
          })
          this.loadOneThumbnail(data.item.name)
          return { ok: true, item: data.item }
        }
        return { ok: false, error: (data && data.detail) || 'Erreur inconnue' }
      } catch (err) {
        return { ok: false, error: this.formatErr(err) }
      } finally {
        this.$delete(this.importingUrls, key)
      }
    },
    async importAllExternalUrls() {
      if (!this.audit || !this.audit.external_refs.length) return
      this.importingBulk = true
      this.bulkErrors = []
      // Liste plate des (ref, url) restants a importer (skip ceux deja importes).
      const targets = this.audit.external_refs.flatMap(ref => ref.urls
        .filter(url => !this.isImportedUrl(ref, url))
        .map(url => ({ ref, url })))
      this.bulkProgress = { done: 0, total: targets.length, failed: [] }
      // Sequentiel : git push ne supporte pas le parallelisme propre sur le
      // meme repo, et on veut une serie de commits unitaires lisibles.
      // reduce + chaining (pas de for-await, eslint).
      await targets.reduce((p, t) => p.then(async () => {
        const result = await this.importOneUrl(t.ref, t.url)
        this.bulkProgress.done += 1
        if (!result || !result.ok) {
          const where = t.ref.is_proposal ? `_proposals/${t.ref.slug}` : t.ref.slug
          const msg = `${where} (${t.url.slice(0, 60)}…) : ${result ? result.error : 'skip'}`
          this.bulkProgress.failed.push(msg)
          this.bulkErrors.push(msg)
        }
      }), Promise.resolve())
      this.importingBulk = false
      // Recharge l'audit pour refleter l'etat reel cote serveur (les MD
      // ont change, certaines URLs ne sont plus presentes).
      this.loadAudit()
    },

    formatSize(bytes) {
      if (!bytes && bytes !== 0) return '—'
      if (bytes < 1024) return `${bytes} o`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
      return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`
    },
    formatErr(err) {
      return (err && err.response && err.response.data && err.response.data.detail)
        || (err && err.message)
        || 'Erreur inconnue'
    },
  },
}
</script>

<style scoped>
.image-library {
  display: flex;
  flex-direction: column;
  height: 75vh;
  position: relative;
}
.image-library--drop-target {
  outline: 2px dashed #7367f0;
  outline-offset: -8px;
  background: rgba(115, 103, 240, 0.04);
}
.image-library__drop-hint {
  position: absolute;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  color: #7367f0;
  pointer-events: none;
  z-index: 3;
}
.image-library__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #e3e3e3;
  flex-shrink: 0;
}
.image-library__search {
  max-width: 320px;
}
.image-library__errors {
  padding: 6px 16px;
  background: #fff3f3;
  border-bottom: 1px solid #f0c0c0;
  flex-shrink: 0;
}
.image-library__content {
  display: flex;
  flex: 1;
  min-height: 0;
}
.image-library__grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  padding: 16px;
  overflow-y: auto;
  align-content: start;
}
.image-library__detail {
  width: 340px;
  border-left: 1px solid #e3e3e3;
  padding: 16px;
  overflow-y: auto;
  flex-shrink: 0;
}
.image-library__detail-preview {
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}
.image-library__meta {
  font-size: 0.85rem;
  margin-bottom: 16px;
}
.image-library__meta dt {
  color: #6e6b7b;
  font-weight: 600;
  margin-top: 8px;
}
.image-library__meta dd {
  margin: 0 0 0 0;
}
.image-library__refs {
  list-style: none;
  padding: 0;
  margin: 0;
}
.image-library__refs li {
  margin: 2px 0;
}

.image-thumb {
  position: relative;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.image-thumb:hover {
  border-color: #7367f0;
}
.image-thumb--selected {
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.2);
}
.image-thumb__preview {
  width: 100%;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8f8f8;
  border-radius: 3px;
  margin-bottom: 6px;
}
.image-thumb__preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.image-thumb__placeholder {
  color: #b0adbe;
}
.image-thumb__name {
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.image-thumb__badge {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 0.65rem;
}

/* Onglets — bandeau au-dessus du contenu */
.image-library__tabs {
  padding: 12px 16px 0 16px;
  border-bottom: 1px solid #e3e3e3;
  background: #fafafa;
}
.image-library__tabs-content {
  /* La tab "Bibliotheque" porte son propre flex/height interne — on laisse
   * passer. La tab "Audit" definit sa hauteur via .audit-panel. */
  min-height: 0;
}

/* Panneau Audit */
.audit-panel {
  height: 75vh;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.audit-section {
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  padding: 12px 16px;
}
.audit-section__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.audit-section__header h6 {
  display: flex;
  align-items: center;
  gap: 6px;
}
.audit-list {
  list-style: none;
  padding: 0;
  margin: 6px 0 0 0;
  font-size: 0.85rem;
}
.audit-list > li {
  padding: 4px 0;
  border-bottom: 1px dashed #f0f0f0;
}
.audit-list > li:last-child {
  border-bottom: none;
}
.audit-list--nested {
  margin: 4px 0 0 16px;
  font-size: 0.8rem;
}
.audit-url {
  word-break: break-all;
}
.audit-import-btn {
  padding: 0 4px;
  margin-left: 6px;
}
.audit-progress {
  margin-bottom: 4px;
}
</style>
